import { ThemePalette } from 'mui.theme';
import { Typography, Grid, Box } from '@mui/material';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import {
  calculateAssetPartsStats,
  percentageDifference,
  getStatColor,
} from './utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useAppSelector } from 'store/hook';
import { useMemo } from 'react';
import { getPercentageDiff } from '../../DashboardV2/Components/AssetDetailsView/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const Stats = ({
  enableDarkTheme,
  data,
  prevData,
  selectedPart,
  selectedAsset,
}: {
  enableDarkTheme: boolean;
  data: any[];
  prevData: any[];
  selectedPart: string[];
  selectedAsset: string[];
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { currDownTimeData, prevDownTimeData } = useAppSelector(
    (state) => state.squaresPartsRecommendationsReducer
  );

  const currentStats = calculateAssetPartsStats(
    data,
    selectedAsset,
    selectedPart
  );
  const totalCurrentDownTimeHrs = useMemo(() => {
    if (
      !Array.isArray(selectedAsset) ||
      selectedAsset.length === 0 ||
      !currDownTimeData?.length
    )
      return 0;
    const selectedIds = new Set(selectedAsset);
    const totalSeconds = currDownTimeData.reduce((sum, item) => {
      if (selectedIds.has(item.asset_id)) {
        return sum + (item.pm_duration ?? 0) + (item.cm_duration ?? 0);
      }
      return sum;
    }, 0);

    const totalHours = totalSeconds / 3600;
    return parseFloat(totalHours.toFixed(2));
  }, [selectedAsset, currDownTimeData]);

  const prevStats = calculateAssetPartsStats(
    prevData,
    selectedAsset,
    selectedPart
  );

  const totalPrevDownTimeHrs = useMemo(() => {
    if (
      !Array.isArray(selectedAsset) ||
      selectedAsset.length === 0 ||
      !prevDownTimeData?.length
    )
      return 0;
    const selectedIds = new Set(selectedAsset);
    const totalSeconds = prevDownTimeData.reduce((sum, item) => {
      if (selectedIds.has(item.asset_id)) {
        return sum + (item.pm_duration ?? 0) + (item.cm_duration ?? 0);
      }
      return sum;
    }, 0);

    const totalHours = totalSeconds / 3600;
    return parseFloat(totalHours.toFixed(2));
  }, [selectedAsset, prevDownTimeData]);

  const cards = [
    {
      title: t('total_number_of_work_orders'),
      value: currentStats.woNumber,
      diff: percentageDifference(prevStats.woNumber, currentStats.woNumber),
      inverse: false,
    },
    {
      title: t('total_parts_replaced'),
      value: currentStats.partsReplaced,
      diff: percentageDifference(
        prevStats.partsReplaced,
        currentStats.partsReplaced
      ),
      inverse: false,
    },
    {
      title: t('total_downtime_hrs'),
      value: totalCurrentDownTimeHrs,
      diff: getPercentageDiff(totalCurrentDownTimeHrs, totalPrevDownTimeHrs),
      inverse: false,
    },
  ];

  return (
    <Grid container spacing={2}>
      {cards.map((card, index) => {
        return (
          <Grid item key={index} lg={4} sm={6} xs={12}>
            <Box
              sx={{
                backgroundColor: enableDarkTheme
                  ? palette.boxBackground
                  : palette.toolBarBackground,
                padding: '20px 10px 20px 10px',
                borderRadius: '5px',
                color: palette.textPrimary,
              }}
            >
              <Typography sx={{ fontSize: '14px' }}>{card.title}</Typography>
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: '700',
                  marginTop: '5px',
                  display: 'flex',
                }}
              >
                {card.diff > 0 ? (
                  <CallMadeIcon
                    sx={{
                      fontSize: '24px',
                      paddingRight: '5px',
                      paddingTop: '6px',
                      color: getStatColor(Number(card.value), card.inverse),
                    }}
                  />
                ) : (
                  <CallReceivedIcon
                    sx={{
                      fontSize: '24px',
                      paddingRight: '5px',
                      paddingTop: '6px',
                      color: getStatColor(Number(card.value), card.inverse),
                    }}
                  />
                )}
                {formatNumberToShortScale(card.value)}
              </Typography>
              <Typography sx={{ fontSize: '12px', marginTop: '5px' }}>
                <span
                  style={{
                    color: getStatColor(Number(card.value), card.inverse),
                  }}
                >
                  {truncateTo2dp(Math.abs(card.diff))}%
                </span>{' '}
                <span>{card.diff > 0 ? 'More' : 'Less'}</span>{' '}
                {t('than_last_period')}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default Stats;
